import { useTranslation } from "react-i18next";
import { Grid, Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components";
import { useShowPriceInVat } from "hooks";
import { CHECKINRESULT_STATUS } from "models";
import { CheckInOptionalServiceActionSelector } from "modules/AppointmentDetails/components/CustomerActions/components";
import "modules/AppointmentDetails/components/CustomerActions/components/CheckInOptionalService/CheckInOptionalService.scss";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { getDisplayedPrice } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { Appointment, CheckInResult } from "models";

type CheckInOptionalServiceProps = {
  title: string;
  results: CheckInResult[];
  canCreateIntervention: boolean;
  canToggleHandled: boolean;
  appointment: Appointment;
  onToggleHandled: (result: CheckInResult) => void;
  onInterventionConverted: (result: CheckInResult) => void;
};

export const CheckInOptionalService = ({
  title,
  results,
  canCreateIntervention,
  canToggleHandled,
  onToggleHandled,
  appointment,
  onInterventionConverted
}: CheckInOptionalServiceProps) => {
  const { t } = useTranslation();
  const appointmentLocation = useAppointmentLocation(appointment);
  const showPriceInVat = useShowPriceInVat();

  if (!results.length) return null;

  return (
    <div className="CustomerActions-items">
      {results.map(result => {
        const notificationEmailItemActive = [CHECKINRESULT_STATUS.Success, CHECKINRESULT_STATUS.Failed].includes(result.status) || result.sent_to_lef;

        return (
          <Grid key={result.id} className="actions-container">
            <Grid.Column width={10}>
              <div className={`icon-container skyblue ${!notificationEmailItemActive ? "disabled" : ""}`}>
                <Popup
                  content={
                    <div className="InfoPopup">
                      <p className="InfoTitle">{title}</p>
                    </div>
                  }
                  trigger={<Icon className="cart" />}
                />
              </div>
              <p className="title">{result.name}</p>
            </Grid.Column>
            <Grid.Column width={2} className="checkin-price-column">
              {result.price > 0 ? <p className="checkin-price">€ {formatPrice(getDisplayedPrice(result.price, appointmentLocation, showPriceInVat))}</p> : null}
            </Grid.Column>
            <Grid.Column width={4}>
              <div className="end-grid">
                <div className="status-container">
                  {(result.notification_email || result.sent_to_lef) && (
                    <div className={`${notificationEmailItemActive ? "notificationEmailActive" : ""}`}>
                      <Popup
                        content={
                          <div>
                            {result.notification_email && (
                              <div>
                                {result.status === CHECKINRESULT_STATUS.Success && (
                                  <div>
                                    {t("v8_sent_to_notification_email", "Sent to {{email}} at {{time}} on {{date}}", {
                                      email: result.notification_email,
                                      time: formatDate(result.updated_on, DATE_FORMATS_FNS.hoursMinutes),
                                      date: formatDate(result.updated_on, DATE_FORMATS_FNS.dateMonthYear)
                                    })}
                                  </div>
                                )}
                                {result.status === CHECKINRESULT_STATUS.Pending && (
                                  <div>
                                    {t("v8_sending_to", "Sending to")} {result.notification_email}
                                  </div>
                                )}
                                {result.status === CHECKINRESULT_STATUS.Failed && (
                                  <>
                                    <div>
                                      {t("v8_failed_to_send_to", "Failed to send to")} {result.notification_email}
                                    </div>
                                    {result.reason && <div>{result.reason}</div>}
                                  </>
                                )}
                              </div>
                            )}
                            {result.notification_email && result.sent_to_lef && <hr />}
                            {result.sent_to_lef && (
                              <div>
                                {t("v8_sent_to_lef_at", "Sent to LEF at {{time}} on {{date}}", {
                                  time: result.sent_to_lef_at ? formatDate(result.sent_to_lef_at, DATE_FORMATS_FNS.hoursMinutes) : "",
                                  date: result.sent_to_lef_at ? formatDate(result.sent_to_lef_at, DATE_FORMATS_FNS.dateMonthYear) : ""
                                })}
                              </div>
                            )}
                          </div>
                        }
                        trigger={
                          <div className="mailTriggerWrapper">
                            <Icon className={`envelope ${notificationEmailItemActive ? "white" : "grey"}`} size="small" />
                          </div>
                        }
                        wide
                      />
                    </div>
                  )}
                  <div className={`changeHandleStatus ${!canToggleHandled ? "disabled" : ""}`} onClick={canToggleHandled ? () => onToggleHandled(result) : undefined}>
                    <Icon className={result.receptionist_handled ? "check circle outline green" : "circle xmark outline red"} />
                  </div>

                  {canCreateIntervention && result.can_convert_to_intervention && (
                    <CheckInOptionalServiceActionSelector result={result} appointment={appointment} onInterventionConverted={() => onInterventionConverted(result)} />
                  )}
                </div>
              </div>
            </Grid.Column>
          </Grid>
        );
      })}
    </div>
  );
};
